import {
  useFirestore,
  useLoadFirestoreDoc,
} from "@/data/studio-react/firebase/useFirestore";
import { useStudioStream } from "@/data/studio-react/useStudioStream";
import { doc } from "firebase/firestore";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";
import { Cart, Customer, Lot } from "types";
import { useAllUninvoicedLotsBoughtAndSold } from "../../useAllLotsBroughtAndSold";

export function useCartForCustomer(
  customer: Customer | null,
  marketId: string,
  setIsTabLoading?: (isLoading: boolean) => void
) {
  let firestore = useFirestore();
  let lotInfo = useStudioStream("sale:lots-info");
  let router = useRouter();
  let customerId = customer?.id || null;
  let saleId = router.query.saleId as string | undefined;

  let docQuery = useMemo(() => {
    return doc(firestore, `markets/${marketId}/carts/${customerId}`);
  }, [firestore, marketId, customerId]);
  let cartLoad = useLoadFirestoreDoc<Cart>(docQuery);

  let extras = cartLoad.data;

  let { lotsBroughtThisSale, lotsSoldThisSale } = useMemo(() => {
    let lotsBroughtThisSale = [] as Lot[];
    let lotsSoldThisSale = [] as Lot[];

    if (!customerId) {
      return { lotsBroughtThisSale, lotsSoldThisSale };
    }

    for (let l of lotInfo.data) {
      if (
        l.buyerCustomerId === customerId &&
        !["Rerun", "Resold"].includes(l.saleStatus ?? "")
      ) {
        if (!l.buyerInvoiceId) {
          lotsBroughtThisSale.push(l);
        }
      }
      if (
        l.sellerCustomerId === customerId &&
        !["Rerun", "Resold"].includes(l.saleStatus ?? "")
      ) {
        if (!l.sellerInvoiceId) {
          lotsSoldThisSale.push(l);
        }
      }
    }

    return { lotsBroughtThisSale, lotsSoldThisSale };
  }, [lotInfo.data, customerId]);

  useEffect(() => {
    if (setIsTabLoading) {
      setIsTabLoading(lotInfo.loading);
    }
  }, [lotInfo.loading]);

  let { boughtLots, soldLots, isLoading } = useAllUninvoicedLotsBoughtAndSold(
    marketId,
    customerId,
    // excludes this sale from the list of lots brought and sold
    saleId
  );

  let { lotsBroughtBySale, lotsSoldBySale, key } = useMemo(() => {
    // group the lots by sale. Show purchases and sales for each sale

    let key = "";

    let lotsBroughtBySale = [] as {
      saleId: string;
      marketId: string;
      lots: Lot[];
    }[];

    let lotsSoldBySale = [] as {
      saleId: string;
      marketId: string;
      lots: Lot[];
    }[];

    if (!customerId) {
      return {
        lotsBroughtBySale,
        lotsSoldBySale,
        key,
      };
    }

    for (let l of lotsBroughtThisSale) {
      key += l.id;

      let sale = lotsBroughtBySale.find(
        (s) => s.saleId === l.saleId && s.marketId === l.marketId
      );
      if (!sale) {
        lotsBroughtBySale.push({
          saleId: l.saleId,
          marketId: l.marketId,
          lots: [l],
        });
      } else {
        // Ensure lot is not already in this sale's lots before adding
        if (!sale.lots.find((existingLot) => existingLot.id === l.id)) {
          sale.lots.push(l);
        }
      }
    }

    for (let l of lotsSoldThisSale) {
      key += l.id;

      let sale = lotsSoldBySale.find(
        (s) => s.saleId === l.saleId && s.marketId === l.marketId
      );
      if (!sale) {
        lotsSoldBySale.push({
          saleId: l.saleId,
          marketId: l.marketId,
          lots: [l],
        });
      } else {
        // Ensure lot is not already in this sale's lots before adding
        if (!sale.lots.find((existingLot) => existingLot.id === l.id)) {
          sale.lots.push(l);
        }
      }
    }

    for (let l of boughtLots) {
      key += l.id;

      let sale = lotsBroughtBySale.find(
        (s) => s.saleId === l.saleId && s.marketId === l.marketId
      );
      if (!sale) {
        lotsBroughtBySale.push({
          saleId: l.saleId,
          marketId: l.marketId,
          lots: [l],
        });
      } else {
        // Ensure lot is not already in this sale's lots before adding
        if (!sale.lots.find((existingLot) => existingLot.id === l.id)) {
          sale.lots.push(l);
        }
      }
    }

    for (let l of soldLots) {
      key += l.id;

      let sale = lotsSoldBySale.find(
        (s) => s.saleId === l.saleId && s.marketId === l.marketId
      );
      if (!sale) {
        lotsSoldBySale.push({
          saleId: l.saleId,
          marketId: l.marketId,
          lots: [l],
        });
      } else {
        // Ensure lot is not already in this sale's lots before adding
        if (!sale.lots.find((existingLot) => existingLot.id === l.id)) {
          sale.lots.push(l);
        }
      }
    }

    return {
      lotsBroughtBySale,
      lotsSoldBySale,
      key,
      isLoading,
    };
  }, [customerId, lotsBroughtThisSale, lotsSoldThisSale, boughtLots, soldLots]);

  return {
    lotsBroughtBySale,
    lotsSoldBySale,
    isLoading,
    extras,
    key,
  };
}
